import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { Markdown } from '@shared/components/typography/Markdown/Markdown';
import PhoneIcon from '@assets/icons/phoneIcon-alt.svg?component';
import TvIcon from '@assets/icons/tvIcon-alt.svg?component';
import WifiIcon from '@assets/icons/wifiIcon.svg?component';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import { ChannelLogoTooltip } from '@components/ProductOffers/misc/ChannelLogoTooltip';
import type { CategoryType, Channel, Plan } from '@t/productOffering.types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface PlanDetailsModalProps {
  open: boolean;
  onClose: () => void;
  plan: Plan | null;
  categoryType?: CategoryType;
}

const CategoryIcons: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  Internet: WifiIcon,
  Video: TvIcon,
  Phone: PhoneIcon,
};

const CategoryIcon = ({ categoryType }: { categoryType?: CategoryType }) => {
  let IconComponent = WifiIcon;
  if (categoryType && CategoryIcons[categoryType]) {
    IconComponent = CategoryIcons[categoryType];
  }
  return <SvgIcon variant="planDetailsCategory" component={IconComponent} inheritViewBox />;
};

export const PlanDetailsModal = ({ open, onClose, plan, categoryType }: PlanDetailsModalProps) => {
  if (!plan) return null;

  const { name, price, description } = plan;
  const { summary, highlights, moreDetails, channelList, includedFeatures } = description;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          // Full-page on phones + bigger-mobile; fixed right-side panel only once
          // the viewport is wider than the panel (tablet up), so it never overflows.
          width: { xs: '100%', md: 676 },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <Paper variant="sideModalHeader">
        <Stack direction="row" gap="12px" alignItems="center">
          <CategoryIcon categoryType={categoryType} />
          <Typography variant="title.plan" className="PlanDetailsModal-title">
            {name}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon className="SideModalHeader-closeIcon" />
        </IconButton>
      </Paper>

      <Box sx={{ padding: '30px 20px' }}>
        <Stack spacing={2.5} useFlexGap>
          <Stack direction="row" alignItems="baseline" spacing={0.5} useFlexGap sx={{ height: 'fit-content' }}>
            <Typography variant="price.hero" className="PlanDetailsModal-price">
              ${price}
            </Typography>
            <Typography variant="price.suffix" className="PlanDetailsModal-price">
              / month
            </Typography>
          </Stack>

          <Typography variant="body.sm" color="text.secondary">
            {summary}
          </Typography>

          <Stack component="ul" spacing={1} useFlexGap sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {highlights.map((text: string, i: number) => (
              <Stack component="li" key={i} direction="row" alignItems="flex-start" spacing={1} useFlexGap>
                <CheckCircleIcon variant="planDetailsHighlight" />
                <Typography variant="body.sm" component="div">
                  <Markdown text={text} />
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Divider variant="planDetailsRule" />

          {/* More Details */}
          {moreDetails.length > 0 && (
            <Stack spacing={1.875} useFlexGap>
              <Typography variant="label.field">More Details</Typography>
              <Stack component="ul" spacing={2.125} useFlexGap sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
                {moreDetails.map((detail: string, i: number) => (
                  <Box component="li" key={i}>
                    <Typography variant="body.sm">{detail}</Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}

          {/* Phone: included features */}
          {includedFeatures && includedFeatures.length > 0 && (
            <Stack>
              <Typography variant="label.field">Included Features</Typography>
              <Stack component="ul" spacing={0.5} useFlexGap sx={{ m: 0, pl: 2.5 }}>
                {includedFeatures.map((feature: string, i: number) => (
                  <Box component="li" key={i}>
                    <Typography variant="body.sm">{feature}</Typography>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}

          {/* TV: channel logos */}
          {channelList && channelList.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(auto-fill, 135px)' },
                gap: 1.5,
                pt: 1,
              }}
            >
              {channelList.map((channel: Channel) => (
                <ChannelLogoTooltip
                  key={channel.name}
                  channel={channel}
                  placement="top"
                  logoWidth="100%"
                  logoHeight="auto"
                  logoPadding="10px"
                  logoBorderRadius="8px"
                />
              ))}
            </Box>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
};
